import React, { useEffect, useState } from "react";
import * as Type from "@interface/index";
import * as Middleware from "@middlewares/index";
import Filter from "@components/Filter";
import PreloaderComponent from "@components/preloader/PreloaderComponent";
import * as Components from "./components";
import Cookie from "../../utils/Cookie";
import FilterButtons from "./components/FilterButtons";
import { NetworkingCardsView } from "./components/NetworkingCardsView";
import { useSelector } from "react-redux";

interface PropsInterface extends Type.ComponentInterface {}

enum EntityType {
  General = 0,
  Gold,
  Member,
  Platinum,
  Silver,
  User,
}

interface Filter {
  specializations?: number[];
  types?: number[];
  city?: number[];
  direction?: number;
}

const NetworkingPage: React.FC<PropsInterface> = () => {
  const [filters, setFilters] = useState<Filter>({});
  const [isAuthClosed, setIsAuthClosed] = useState<boolean>(
    !!Cookie.get("isAuthClosed")
  );
  const [isUnAuthClosed, setIsUnAuthClosed] = useState<boolean>(
    !!Cookie.get("isUnAuthClosed")
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showCompanies, setShowCompanies] = useState<boolean>(true);
  const [showUsers, setShowUsers] = useState<boolean>(true);
  const [typeCatalog, setTypeCatalog] = useState<string>("all");

  const authStatus = useSelector((state) => state.Auth?.status);

  const [toggleReloadUseEffect, setToggleReloadUseEffect] = useState(0);

  useEffect(() => {
    reload();
  }, [toggleReloadUseEffect]);

  useEffect(() => {
    document.title = "Networking | ОТМ: WINTER 25/26";
    Middleware.Networking.User.count();
    Middleware.Networking.Company.count();
    Middleware.Company.Type.load();
    Middleware.Company.Specialization.load();
    Middleware.Company.Direction.load();
    Middleware.Company.City.load();
    window.scrollTo(0, 0);

    if (!isAuthClosed && authStatus === "authorized") {
      Middleware.Ui.Modal.open("HowToUseAuth");
      setIsAuthClosed(true);
      Cookie.set("isAuthClosed", "true", {
        expires: new Date("Mar 01 2032 18:00:00"),
      });
    }

    if (!isUnAuthClosed && authStatus === "unauthorized") {
      Middleware.Ui.Modal.open("HowToUseUnAuth");
      setIsUnAuthClosed(true);
      Cookie.set("isUnAuthClosed", "true", {
        expires: new Date("Mar 01 2032 18:00:00"),
      });
    }
  }, []);

  const clear = () => {
    setSearchQuery("");
    Middleware.Networking.Search.clear();
  };

  const reload = () => {
    Middleware.Networking.Exponent.General.page(1, filters);
    Middleware.Networking.Exponent.Platinum.page(1, filters);
    Middleware.Networking.Exponent.Gold.page(1, filters);
    Middleware.Networking.Exponent.Silver.page(1, filters);
    Middleware.Networking.Member.clear();
    Middleware.Networking.Member.page(1, filters);
    Middleware.Networking.User.clear();
    Middleware.Networking.User.page(1, filters);
  };

  const load = (type: EntityType, page: number) => {
    switch (type) {
      case EntityType.Member:
        Middleware.Networking.Member.addPage(page, filters);
        break;
      case EntityType.User:
        Middleware.Networking.User.addPage(page, filters);
        break;
    }
  };

  const handleClick = () => {
    const howToUse =
      authStatus === "authorized" ? "HowToUseAuth" : "HowToUseUnAuth";

    Middleware.Ui.Modal.open(howToUse);
  };

  const handleFilterButton = (catalog: string) => {
    setTypeCatalog(catalog);
  };

  const workshopData = useSelector((state: any) => state.Main?.Workshop?.data);
  const netwSearchList = useSelector(
    (state: any) => state.Networking.Search.list
  );
  const exponentGeneral = useSelector(
    (state: any) => state.Networking.Exponent.General
  );
  const exponentPlatinum = useSelector(
    (state: any) => state.Networking.Exponent.Platinum
  );
  const exponentGold = useSelector(
    (state: any) => state.Networking.Exponent.Gold
  );
  const exponentSilver = useSelector(
    (state: any) => state.Networking.Exponent.Silver
  );
  const member = useSelector((state: any) => state.Networking.Member);
  const networkingUser = useSelector((state: any) => state.Networking.User);

  const otmTitle = workshopData?.title?.replace("ОТМ: ", "");

  return (
    <main className="main main--netw">
      <div className="netw">
        <div className="netw__header">
          <div className="cont">
            <p className="netw__top">ОТМ Networking</p>

            <h1 className="netw__title">Начните сотрудничество прямо сейчас</h1>

            <div className="netw__desc">
              <p>
                В нетворкинге участвуют представители туристических ведомств и
                дестинаций, отелей и авиакомпаний, турагенты и туроператоры.
                Здесь вы можете найти партнеров для сотрудничества или коллег
                для обмена опытом.
              </p>
            </div>
            <button
              className="btn btn--red-underline"
              onClick={() => handleClick()}
            >
              Как пользоваться?
            </button>
          </div>
        </div>

        <div className="cont">
          <div className="netw__header-filters">
            <div className="netw__filter-title">Каталог</div>
            <div className="netw__panel-wrapper">
              <FilterButtons
                catalog={typeCatalog}
                onClick={handleFilterButton}
              />
            </div>
            <div className="netw__search-wrapper">
              <input
                className="netw__search"
                id="search"
                type="text"
                name="search"
                value={searchQuery}
                placeholder="Найти представителя"
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (e.target.value.length > 2) {
                    Middleware.Networking.Search.list(e.target.value);
                  }
                  if (!e.target.value.length) {
                    Middleware.Networking.Search.clear();
                  }
                }}
              />
            </div>
          </div>

          <hr className="w-full col-start-1 col-end-3 !my-10 border-0 bg-light-gray h-[1px]" />

          <Filter
            isNetworkingPage={true}
            isAdaptive={true}
            catalog={typeCatalog}
            onClick={handleFilterButton}
            onClearQuery={clear}
            callback={(
              newFilters: any,
              newViewCompanies: any,
              newViewUsers: any
            ) => {
              setFilters(newFilters);
              setShowCompanies(newViewCompanies);
              setShowUsers(newViewUsers);
              setToggleReloadUseEffect(new Date().getTime());
            }}
          />

          <div className="flex flex-col gap-16 pb-10">
            {!netwSearchList ? (
              <>
                {networkingUser?.list?.length > 0 && showUsers && (
                  <NetworkingCardsView
                    title={"Представители компаний"}
                    isListLoading={networkingUser?.status === "loading"}
                    pageCount={networkingUser?.paginator?.pageCount}
                    currentPage={networkingUser?.paginator?.page}
                    loadMore={() =>
                      load(EntityType.User, networkingUser?.paginator?.page + 1)
                    }
                  >
                    <div className="netw__group-body">
                      {networkingUser?.list?.map((user: any) => {
                        return (
                          <Components.UserCard key={user.id} user={user} />
                        );
                      })}
                    </div>
                  </NetworkingCardsView>
                )}
                {exponentGeneral?.status !== "loaded" ||
                exponentPlatinum?.status !== "loaded" ||
                exponentGold?.status !== "loaded" ||
                exponentSilver?.status !== "loaded" ||
                member?.status !== "loaded" ||
                networkingUser?.status !== "loaded" ? (
                  <div className="netw__group-preloader">
                    <PreloaderComponent />
                  </div>
                ) : (
                  ""
                )}
                {exponentGeneral?.data?.length > 0 && showCompanies && (
                  <NetworkingCardsView
                    title={
                      <p>
                        Организатор OTM: <span>{otmTitle}</span>
                      </p>
                    }
                  >
                    <div className="netw__group-body">
                      {exponentGeneral?.data?.map(
                        (General: any, index: number) => {
                          return (
                            <Components.CompanyCard
                              key={index}
                              type="general"
                              standId={General.id}
                              imageUrl={General.standImage}
                              companyLogoUrl={General.companyImage}
                              title={General.title}
                              description={General.description}
                              contacts={General.users}
                            />
                          );
                        }
                      )}
                    </div>
                  </NetworkingCardsView>
                )}
                {exponentPlatinum?.data?.length > 0 && showCompanies && (
                  <NetworkingCardsView
                    title={
                      <p>
                        Партнеры OTM: <span>{otmTitle}</span>
                      </p>
                    }
                  >
                    <div className="netw__group-body netw__group-body--platinum">
                      {exponentPlatinum?.data?.map(
                        (Platinum: any, index: number) => {
                          return (
                            <Components.CompanyCard
                              key={index}
                              type="platinum"
                              standId={Platinum.id}
                              imageUrl={Platinum.standImage}
                              companyLogoUrl={Platinum.companyImage}
                              title={Platinum.title}
                              description={Platinum.description}
                              contacts={Platinum.users}
                            />
                          );
                        }
                      )}
                    </div>
                  </NetworkingCardsView>
                )}
                {(exponentGold?.data?.length > 0 ||
                  exponentSilver?.data?.length > 0) &&
                  showCompanies && (
                    <NetworkingCardsView
                      title={
                        <p>
                          Участники OTM: <span>{otmTitle}</span>
                        </p>
                      }
                    >
                      <div className="netw__group-body">
                        {exponentGold?.data?.map((Gold: any, index: number) => {
                          return (
                            <Components.CompanyCard
                              key={index}
                              type="gold"
                              standId={Gold.id}
                              imageUrl={Gold.standImage}
                              companyLogoUrl={Gold.companyImage}
                              title={Gold.title}
                              description={Gold.description}
                              contacts={Gold.users}
                            />
                          );
                        })}
                        {exponentSilver?.data?.map(
                          (Silver: any, index: number) => {
                            return (
                              <Components.CompanyCard
                                key={index}
                                type="silver"
                                standId={Silver.id}
                                imageUrl={Silver.standImage}
                                companyLogoUrl={Silver.companyImage}
                                title={Silver.title}
                                description={Silver.description}
                                contacts={
                                  [
                                    // {
                                    //   avatarUrl: require("@public/images/stands-contact-1.jpg"),
                                    //   name: "",
                                    // },
                                  ]
                                }
                              />
                            );
                          }
                        )}
                      </div>
                    </NetworkingCardsView>
                  )}
                {member?.list?.length > 0 && showCompanies && (
                  <NetworkingCardsView
                    title={"Другие компании"}
                    currentPage={member?.paginator?.page}
                    pageCount={member?.paginator?.pageCount}
                    isListLoading={member?.status === "loading"}
                    loadMore={() =>
                      load(EntityType.Member, member.paginator?.page + 1)
                    }
                  >
                    <div className="netw__group-body">
                      {member?.list?.map((Member: any, index: number) => {
                        return (
                          <Components.CompanyCard
                            url={`/company/${Member.id}`}
                            key={index}
                            standId={Member.id}
                            type="bronze"
                            imageUrl={require("@public/images/stands-bg-1@2x.jpg")}
                            companyLogoUrl={Member.companyImage}
                            title={Member.title}
                            description=""
                            contacts={
                              [
                                // {
                                //   avatarUrl: require("@public/images/stands-contact-1.jpg"),
                                //   name: "",
                                // },
                              ]
                            }
                          />
                        );
                      })}
                    </div>
                  </NetworkingCardsView>
                )}
              </>
            ) : (
              <div className="netw__result">
                <div className="netw__result-title">Результаты поиска</div>
                <hr />
                <div className="netw__result-search">
                  Вы искали: <span>«{searchQuery}»</span>
                </div>
                {netwSearchList?.map((user: any) => {
                  return <Components.ResultCard key={user.id} user={user} />;
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default NetworkingPage;
